import { Link } from 'react-router-dom';
import useReveal from '../hooks/useReveal';
import './ServiceDetail.css';
import { config } from '../config';

const CHANNELS = ['SEO','Google Ads','Meta Ads','Email Marketing','Content Strategy','Social Media','YouTube Ads','Influencer','WhatsApp Marketing','Analytics','CRO','LinkedIn Ads'];

const DM_SERVICES = [
  { icon:'🔍', title:'Search Engine Optimisation', desc:'Technical SEO, on-page optimisation, link building and content strategy that ranks your site on page one - and keeps it there.' },
  { icon:'📣', title:'Google & Meta Ads', desc:'ROI-focused paid campaigns on Google Search, Display, YouTube, Facebook, and Instagram. Every rupee tracked and optimised.' },
  { icon:'📧', title:'Email Marketing', desc:'Personalised email sequences, newsletters, and drip campaigns that nurture leads and drive repeat purchases automatically.' },
  { icon:'📱', title:'Social Media Management', desc:'Strategy, content creation, scheduling, and community management across Instagram, LinkedIn, Facebook, and X.' },
  { icon:'✍️', title:'Content Marketing', desc:'SEO-driven blogs, case studies, whitepapers, and video scripts that establish your brand as the industry authority.' },
  { icon:'📊', title:'Analytics & Reporting', desc:'Clear monthly reports showing exactly what is working - traffic, conversions, ROAS, and actionable next steps.' },
  { icon:'🎯', title:'Conversion Rate Optimisation', desc:'Landing page audits, A/B testing, heatmaps, and UX improvements that turn more visitors into paying customers.' },
  { icon:'🤝', title:'Influencer Marketing', desc:'Identifying, vetting, and managing influencer partnerships that generate authentic reach and measurable brand trust.' },
];

const RESULTS = [
  { val:'3.2x', label:'Average ROAS We Deliver' },
  { val:'68%',  label:'Avg Organic Traffic Growth (6 mo)' },
  { val:'45%',  label:'Lower Cost-Per-Lead vs Industry Avg' },
  { val:'120+', label:'Campaigns Successfully Managed' },
];

const PROCESS = [
  { n:'01', title:'Audit & Research',    desc:'Complete audit of your current digital presence, competitor analysis, and audience research to find the highest-impact opportunities.' },
  { n:'02', title:'Strategy Blueprint',  desc:'A detailed, channel-specific growth plan with clear KPIs, budgets, and 90-day milestones tailored to your business goals.' },
  { n:'03', title:'Campaign Launch',     desc:'Campaigns built, copy written, creatives designed, and everything live - with tracking and conversion goals verified.' },
  { n:'04', title:'Optimise & Scale',   desc:'Weekly performance reviews, A/B testing, and budget reallocation to maximise ROI. We scale what works, cut what does not.' },
];

const FAQS = [
  { q:'How long before I see results from SEO?',  a:'SEO typically shows meaningful results in 3–6 months. Paid ads can drive traffic from day one. We set realistic expectations upfront and report clearly every month.' },
  { q:'What budget do I need for paid advertising?', a:'We work with ad budgets from ₹15,000/month upwards. Our management fee is separate. We will recommend a budget based on your goals and market competition.' },
  { q:'Do you handle ad creatives and copywriting?', a:'Yes. Our team handles everything - strategy, copywriting, creative design, campaign setup, and ongoing optimisation. You just approve the work.' },
  { q:'How do you measure success?',              a:'We track KPIs agreed upfront - organic traffic, keyword rankings, leads, ROAS, CPL, and conversions. Monthly reports are clear, visual, and jargon-free.' },
];

export default function DigitalMarketing(){
  useReveal();
  const marquee = [...CHANNELS,...CHANNELS];
  const BARS = [40,65,50,80,70,90,75,95];
  const MONTHS = ['May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  return(
    <div className="sd-page">

      {/* HERO */}
      <section className="sd-hero">
        <div className="sd-hero__bg" style={{background:'radial-gradient(ellipse 70% 60% at 60% 30%,rgba(79,142,247,.07) 0%,transparent 70%),radial-gradient(ellipse 40% 40% at 10% 80%,rgba(201,168,76,.06) 0%,transparent 60%)'}}/>
        <div className="sd-hero__grid"/>
        <div className="container sd-hero__inner">
          <div className="sd-hero__content">
            <span className="sd-eyebrow" style={{background:'rgba(79,142,247,.08)',borderColor:'rgba(79,142,247,.2)',color:'var(--accent2)'}}>
              Digital Marketing
            </span>
            <h1 className="sd-hero__title">
              Growth-Driven Marketing<br/>
              That Delivers <span className="g">Real ROI</span>
            </h1>
            <p className="sd-hero__sub">
              Data-driven campaigns that connect your brand with the right audience at the right moment. Full-funnel strategies across every major digital channel - all measurable, all optimised.
            </p>
            <div className="sd-hero__btns">
              {/* <Link to={`${config.appUrl}/contact`} className="btn-primary">Get Free Audit →</Link> */}
              <Link to={`${config.appUrl}/contact`} className="btn-primary">Book a Strategy Call →</Link>
            </div>
            <div className="sd-hero__stats">
              {RESULTS.map(r=>(
                <div className="sd-stat" key={r.label}>
                  <span>{r.val}</span>
                  <p>{r.label}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="sd-hero__visual rr">
            <div className="sd-chart">
              <div className="sd-chart__label">Organic Traffic Growth</div>
              <div className="sd-chart__bars">
                {BARS.map((h,i)=>(
                  <div key={i} className="sd-chart__bar" style={{height:h+'%',animationDelay:i*0.1+'s'}}/>
                ))}
              </div>
              <div className="sd-chart__months">
                {MONTHS.map(m=><span key={m}>{m}</span>)}
              </div>
              <div className="sd-chart__kpis">
                <div className="sd-chart__kpi">
                  <div className="sd-chart__kpi-val">+102%</div>
                  <div className="sd-chart__kpi-label">Traffic Growth</div>
                </div>
                <div className="sd-chart__kpi">
                  <div className="sd-chart__kpi-val">2.2x</div>
                  <div className="sd-chart__kpi-label">ROAS</div>
                </div>
                <div className="sd-chart__kpi">
                  <div className="sd-chart__kpi-val">-45%</div>
                  <div className="sd-chart__kpi-label">Cost Per Lead</div>
                </div>
                <div className="sd-chart__kpi">
                  <div className="sd-chart__kpi-val">89%</div>
                  <div className="sd-chart__kpi-label">Retention Rate</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CHANNELS MARQUEE */}
      <div className="marquee-section">
        <div className="marquee-track">
          {marquee.map((t,i)=>(
            <div className="marquee-item" key={i}><span className="dot"/>{t}</div>
          ))}
        </div>
      </div>

      {/* SERVICES */}
      <section className="sd-features">
        <div className="container">
          <div className="tc reveal">
            <span className="s-eye">What We Offer</span>
            <h2 className="s-title">Every Channel. <span className="g">Every Advantage.</span></h2>
            <p className="s-body">A full suite of digital marketing services - managed by specialists, powered by data, focused on your growth.</p>
          </div>
          <div className="dm-services-grid">
            {DM_SERVICES.map((s,i)=>(
              <div key={s.title} className={`dm-svc-card reveal d${(i%2)+1}`}>
                <div className="dm-svc-card__icon">{s.icon}</div>
                <div>
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="sd-process">
        <div className="container">
          <div className="tc reveal">
            <span className="s-eye">How We Work</span>
            <h2 className="s-title">Our Marketing <span className="g">Process</span></h2>
          </div>
          <div className="sd-proc-grid service-digital">
            {PROCESS.map((p,i)=>(
              <div key={p.n} className={`sd-proc-card reveal d${i+1}`} data-n={p.n}>
                <div className="proc-num">{p.n}</div>
                <h3>{p.title}</h3>
                <p>{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="sd-faq">
        <div className="container">
          <div className="tc reveal">
            <span className="s-eye">Got Questions?</span>
            <h2 className="s-title">Common <span className="g">Questions</span></h2>
          </div>
          <div className="sd-faq-grid">
            {FAQS.map((f,i)=>(
              <div key={i} className={`faq-card reveal d${(i%2)+1}`}>
                <div className="faq-q"><span className="faq-icon">✦</span>{f.q}</div>
                <p className="faq-a">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <div className="cta-sect">
        <div className="cta-glow"/>
        <div className="container" style={{position:'relative',textAlign:'center'}}>
          <div className="reveal">
            <h2 className="cta-title">Ready to <span className="g">Grow Your Brand?</span></h2>
            <p className="cta-body">Get a free digital marketing audit - we will show you exactly where you are leaving money on the table.</p>
            <div style={{display:'flex',gap:'1rem',justifyContent:'center',flexWrap:'wrap'}}>
              <Link to={`${config.appUrl}/contact`} className="btn-primary">Get Free Audit →</Link>
              {/* <Link to={`${config.appUrl}/contact`} className="btn-outline">Book Strategy Call</Link> */}
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}